import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Container,
} from '@mui/material';
import { Send } from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email || '').trim());
};

const DemoRequest = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companyName: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch(`${API_BASE}/api/demo-request/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          company_name: formData.companyName.trim(),
          message: formData.message.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitted(true);
      } else {
        setErrors({
          submit: data.error || 'Unable to submit. Please try again.',
          ...(data.field && { [data.field]: data.error }),
        });
      }
    } catch (error) {
      setErrors({ submit: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Paper sx={{ maxWidth: 440, mx: 'auto', p: 4, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Thank You!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We have received your demo request and will contact you shortly to schedule your demo.
          </Typography>
          <Button component={Link} to="/" variant="contained">
            Back to Home
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6, px: 2 }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Request a Demo
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Schedule a personalized demo to see how Firmflow can transform your practice.
          </Typography>
        </Box>

        <Paper component="form" onSubmit={handleSubmit} sx={{ p: 4 }}>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrors((p) => ({ ...p, submit: '' }))}>
              {errors.submit}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Name *"
            value={formData.name}
            onChange={(e) => updateFormData('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="John Doe"
            sx={{ mb: 2 }}
            disabled={isSubmitting}
          />
          <TextField
            fullWidth
            type="email"
            label="Email *"
            value={formData.email}
            onChange={(e) => updateFormData('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            placeholder="john@company.com"
            sx={{ mb: 2 }}
            disabled={isSubmitting}
          />
          <TextField
            fullWidth
            label="Company Name *"
            value={formData.companyName}
            onChange={(e) => updateFormData('companyName', e.target.value)}
            error={!!errors.companyName}
            helperText={errors.companyName}
            placeholder="Your Company LLC"
            sx={{ mb: 2 }}
            disabled={isSubmitting}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message"
            value={formData.message}
            onChange={(e) => updateFormData('message', e.target.value)}
            placeholder="Tell us about your firm and what you'd like to learn..."
            sx={{ mb: 3 }}
            disabled={isSubmitting}
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button component={Link} to="/" variant="outlined" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={isSubmitting ? null : <Send />}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Demo Request'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DemoRequest;
